"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkConditionals = void 0;
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const helpers_1 = require("../../../utils/helpers");
// Check interview response with conditions, returning the result and null.
const checkConditionals = ({ interview, conditionals }) => {
    let mathExpression = ''; // Construct the math expression to be evaluated
    // Iterate through the provided conditionals
    conditionals.forEach((conditional, index) => {
        // Extract components of the conditional
        const { logicalOperator, path, comparisonOperator, value, parentheses } = conditional;
        // Replace response placeholders specified between brackets in a path by the corresponding value in the interview response.
        const interpolatedPath = (0, helpers_1.interpolatePath)(interview, path);
        // Get the response for the interpolated path
        const response = (0, helpers_1.getResponse)(interview, interpolatedPath, null);
        let conditionMet;
        // Evaluate if the condition is met
        if (value === 'null') {
            // For value 'null', check if the response is null
            switch (comparisonOperator) {
                case '===':
                    if (Array.isArray(response)) {
                        // For Array
                        conditionMet = response.length === 0;
                    }
                    else {
                        // For String or Boolean
                        conditionMet = response === null;
                    }
                    break;
                case '!==':
                    if (Array.isArray(response)) {
                        // For Array
                        conditionMet = response.length !== 0;
                    }
                    else {
                        // For String or Boolean
                        conditionMet = response !== null;
                    }
                    break;
                default:
                    conditionMet = false;
                    break;
            }
        }
        else if ((typeof response === 'string' && typeof value === 'string') ||
            (typeof response === 'boolean' && typeof value === 'boolean')) {
            // For String or Boolean
            switch (comparisonOperator) {
                case '===':
                    conditionMet = response === value;
                    break;
                case '!==':
                    conditionMet = response !== value;
                    break;
                default:
                    conditionMet = false;
                    break;
            }
        }
        else if (typeof value === 'number') {
            // For Number
            switch (comparisonOperator) {
                case '===':
                    conditionMet = Number(response) === value;
                    break;
                case '!==':
                    conditionMet = Number(response) !== value;
                    break;
                case '>':
                    conditionMet = Number(response) > value;
                    break;
                case '<':
                    conditionMet = Number(response) < value;
                    break;
                case '>=':
                    conditionMet = Number(response) >= value;
                    break;
                case '<=':
                    conditionMet = Number(response) <= value;
                    break;
                default:
                    conditionMet = false;
                    break;
            }
        }
        else if (Array.isArray(response)) {
            // For Array
            switch (comparisonOperator) {
                case '===':
                    conditionMet = response.includes(value);
                    break;
                case '!==':
                    conditionMet = !response.includes(value);
                    break;
                default:
                    conditionMet = false;
                    break;
            }
        }
        else {
            // Handle other response types if necessary
            conditionMet = false;
        }
        const parenthesesStart = parentheses === '(' ? '(' : ''; // Add an opening parentheses if necessary
        const parenthesesEnd = parentheses === ')' ? ')' : ''; // Add a closing parentheses if necessary
        if (index === 0) {
            // For the first condition, initialize the result
            mathExpression = parenthesesStart + conditionMet + parenthesesEnd;
        }
        else if (logicalOperator === '||') {
            mathExpression += ' || ' + parenthesesStart + conditionMet + parenthesesEnd; // Add the result to the final result
        }
        else if (logicalOperator === '&&') {
            mathExpression += ' && ' + parenthesesStart + conditionMet + parenthesesEnd; // Add the result to the final result
        }
    });
    // FIXME: This eval() is a security risk, and should be replaced with a safer alternative
    // Evaluate the final result using eval() to handle logical operators
    const finalResult = eval(mathExpression);
    // Return the final result along with null (as per the function signature)
    return [finalResult, null];
};
exports.checkConditionals = checkConditionals;
//# sourceMappingURL=checkConditionals.js.map